
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  AlertCircle, 
  Plus, 
  X, 
  Save, 
  Gift, 
  Tag, 
  Ticket,
  ShoppingBag,
  CalendarDays
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Sample products for product selection
const sampleProducts = [
  { id: "P001", name: "Premium Coffee", price: 12.90 },
  { id: "P002", name: "Chocolate Bar", price: 5.50 },
  { id: "P003", name: "Mineral Water (500ml)", price: 1.80 },
  { id: "P004", name: "Bread Loaf", price: 4.20 },
  { id: "P005", name: "Fresh Milk (1L)", price: 7.30 }
];

const RedemptionRulesTab = () => {
  const { toast } = useToast();
  
  // Cash Voucher Redemptions
  const [cashVouchers, setCashVouchers] = useState([
    { id: 1, minPoints: 500, valueRM: 5 },
    { id: 2, minPoints: 1000, valueRM: 10 }
  ]);
  
  // Free Item Redemptions
  const [freeItems, setFreeItems] = useState([
    { id: 1, productId: "P001", productName: "Premium Coffee", pointsRequired: 200 },
    { id: 2, productId: "P003", productName: "Mineral Water (500ml)", pointsRequired: 50 }
  ]);
  
  // Discount Redemptions
  const [discounts, setDiscounts] = useState([
    { id: 1, discountPercent: 10, minSpend: 50, pointsRequired: 300 },
    { id: 2, discountPercent: 20, minSpend: 100, pointsRequired: 700 }
  ]);
  
  // Limitations
  const [limitations, setLimitations] = useState({
    maxRedemptionsPerDay: 3,
    eligibleTier: "all",
    channels: {
      pos: true,
      online: true
    }
  });
  
  // Add new cash voucher
  const addCashVoucher = () => {
    const newId = cashVouchers.length > 0 ? Math.max(...cashVouchers.map(v => v.id)) + 1 : 1;
    setCashVouchers([...cashVouchers, { id: newId, minPoints: 0, valueRM: 0 }]);
  };
  
  // Add new free item
  const addFreeItem = () => {
    const newId = freeItems.length > 0 ? Math.max(...freeItems.map(i => i.id)) + 1 : 1;
    setFreeItems([...freeItems, { id: newId, productId: "", productName: "", pointsRequired: 0 }]);
  };
  
  // Add new discount
  const addDiscount = () => {
    const newId = discounts.length > 0 ? Math.max(...discounts.map(d => d.id)) + 1 : 1;
    setDiscounts([...discounts, { id: newId, discountPercent: 0, minSpend: 0, pointsRequired: 0 }]);
  };
  
  // Remove cash voucher
  const removeCashVoucher = (id: number) => {
    setCashVouchers(cashVouchers.filter(v => v.id !== id));
  };
  
  // Remove free item
  const removeFreeItem = (id: number) => {
    setFreeItems(freeItems.filter(i => i.id !== id));
  };
  
  // Remove discount
  const removeDiscount = (id: number) => {
    setDiscounts(discounts.filter(d => d.id !== id));
  };
  
  // Update cash voucher
  const updateCashVoucher = (id: number, field: string, value: number) => {
    setCashVouchers(
      cashVouchers.map(voucher => {
        if (voucher.id === id) {
          return { ...voucher, [field]: value };
        }
        return voucher;
      })
    );
  };
  
  // Update free item
  const updateFreeItem = (id: number, field: string, value: string | number) => {
    setFreeItems(
      freeItems.map(item => {
        if (item.id === id) {
          if (field === "productId") {
            // If selecting a product, update both ID and name
            const selectedProduct = sampleProducts.find(p => p.id === value);
            if (selectedProduct) {
              return {
                ...item,
                productId: selectedProduct.id,
                productName: selectedProduct.name
              };
            }
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
  };
  
  // Update discount
  const updateDiscount = (id: number, field: string, value: number) => {
    setDiscounts(
      discounts.map(discount => {
        if (discount.id === id) {
          return { ...discount, [field]: value };
        }
        return discount;
      })
    );
  };
  
  // Save all settings
  const saveSettings = () => {
    // In a real app, this would save to IndexedDB and sync to the server when online
    console.log("Saving redemption rules:", {
      cashVouchers,
      freeItems,
      discounts,
      limitations
    });
    
    toast({
      title: "Settings Saved",
      description: "Redemption rules have been updated successfully."
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Redemption Rules</h2>
        <Button onClick={saveSettings}>
          <Save className="h-4 w-4 mr-2" /> Save All Rules
        </Button>
      </div>
      
      <Tabs defaultValue="cash-voucher" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="cash-voucher">
            <Ticket className="h-4 w-4 mr-2" /> Cash Voucher
          </TabsTrigger>
          <TabsTrigger value="free-item">
            <Gift className="h-4 w-4 mr-2" /> Free Item
          </TabsTrigger>
          <TabsTrigger value="discount">
            <Tag className="h-4 w-4 mr-2" /> Discount
          </TabsTrigger>
        </TabsList>
        
        {/* Cash Voucher Tab */}
        <TabsContent value="cash-voucher" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Cash Voucher Redemptions</CardTitle>
                  <CardDescription>
                    Set up point values for cash vouchers
                  </CardDescription>
                </div>
                <Button onClick={addCashVoucher} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Voucher
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {cashVouchers.map((voucher) => (
                <div key={voucher.id} className="flex items-end gap-4 p-4 border rounded-md">
                  <div className="flex-1">
                    <Label>Minimum Points</Label>
                    <Input
                      type="number"
                      value={voucher.minPoints}
                      onChange={(e) => updateCashVoucher(voucher.id, 'minPoints', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Value (RM)</Label>
                    <Input
                      type="number"
                      value={voucher.valueRM}
                      onChange={(e) => updateCashVoucher(voucher.id, 'valueRM', parseFloat(e.target.value))}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCashVoucher(voucher.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {cashVouchers.length === 0 && (
                <div className="flex items-center justify-center p-4 border border-dashed rounded-md">
                  <p className="text-muted-foreground">No cash vouchers configured. Add one to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Free Item Tab */}
        <TabsContent value="free-item" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Free Item Redemptions</CardTitle>
                  <CardDescription>
                    Set up products that can be redeemed with points
                  </CardDescription>
                </div>
                <Button onClick={addFreeItem} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Product
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {freeItems.map((item) => (
                <div key={item.id} className="flex items-end gap-4 p-4 border rounded-md">
                  <div className="flex-1">
                    <Label>Select Product</Label>
                    <Select
                      value={item.productId}
                      onValueChange={(value) => updateFreeItem(item.id, 'productId', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent>
                        {sampleProducts.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} (RM{product.price.toFixed(2)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Points Required</Label>
                    <Input
                      type="number"
                      value={item.pointsRequired}
                      onChange={(e) => updateFreeItem(item.id, 'pointsRequired', parseInt(e.target.value))}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFreeItem(item.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {freeItems.length === 0 && (
                <div className="flex items-center justify-center p-4 border border-dashed rounded-md">
                  <p className="text-muted-foreground">No free item redemptions configured. Add one to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Discount Tab */}
        <TabsContent value="discount" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Discount Redemptions</CardTitle>
                  <CardDescription>
                    Set up percentage discounts that can be redeemed with points
                  </CardDescription>
                </div>
                <Button onClick={addDiscount} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Discount
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {discounts.map((discount) => (
                <div key={discount.id} className="flex items-end gap-4 p-4 border rounded-md">
                  <div className="flex-1">
                    <Label>Discount Percentage</Label>
                    <div className="flex items-center">
                      <Input
                        type="number"
                        value={discount.discountPercent}
                        onChange={(e) => updateDiscount(discount.id, 'discountPercent', parseInt(e.target.value))}
                      />
                      <span className="ml-2">%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label>Minimum Spend (RM)</Label>
                    <Input
                      type="number"
                      value={discount.minSpend}
                      onChange={(e) => updateDiscount(discount.id, 'minSpend', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Points Required</Label>
                    <Input
                      type="number"
                      value={discount.pointsRequired}
                      onChange={(e) => updateDiscount(discount.id, 'pointsRequired', parseInt(e.target.value))}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDiscount(discount.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {discounts.length === 0 && (
                <div className="flex items-center justify-center p-4 border border-dashed rounded-md">
                  <p className="text-muted-foreground">No discount redemptions configured. Add one to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Redemption Limitations */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Redemption Limitations</CardTitle>
          <CardDescription>
            Set general limitations on redemption rules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="maxRedemptions">Max Redemptions Per Day</Label>
              <Input
                id="maxRedemptions"
                type="number"
                value={limitations.maxRedemptionsPerDay}
                onChange={(e) => setLimitations({
                  ...limitations,
                  maxRedemptionsPerDay: parseInt(e.target.value)
                })}
              />
              <p className="text-sm text-muted-foreground">
                Set to 0 for unlimited redemptions per day
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="eligibleTier">Eligible Member Tier</Label>
              <Select
                value={limitations.eligibleTier}
                onValueChange={(value) => setLimitations({
                  ...limitations,
                  eligibleTier: value
                })}
              >
                <SelectTrigger id="eligibleTier">
                  <SelectValue placeholder="Select eligible tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="silver">Silver & Above</SelectItem>
                  <SelectItem value="gold">Gold Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Redemption Channels</Label>
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2">
                <Switch
                  id="pos-channel"
                  checked={limitations.channels.pos}
                  onCheckedChange={(checked) => setLimitations({
                    ...limitations,
                    channels: { ...limitations.channels, pos: checked }
                  })}
                />
                <Label htmlFor="pos-channel" className="cursor-pointer">
                  <ShoppingBag className="h-4 w-4 inline mr-2" /> POS
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="online-channel"
                  checked={limitations.channels.online}
                  onCheckedChange={(checked) => setLimitations({
                    ...limitations,
                    channels: { ...limitations.channels, online: checked }
                  })}
                />
                <Label htmlFor="online-channel" className="cursor-pointer">
                  <CalendarDays className="h-4 w-4 inline mr-2" /> Online Store
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button variant="outline" className="w-full" onClick={saveSettings}>
            <Save className="h-4 w-4 mr-2" /> Save Limitations
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RedemptionRulesTab;
